import {
  formatPreviewDate,
  parseDateInputValue,
  parseLineItemDescription,
} from "./billingPeriodUtils";
import type { Invoice, InvoiceStatus } from "./types";

export const INVOICE_PREVIEWS_QUERY_KEY = ["invoice-previews"] as const;

export interface InvoiceApiItem {
  id?: string | number;
  description?: string;
  hsn_sac?: string;
  qty?: string | number;
  rate?: string | number;
  amount?: string | number;
  cgst?: string | number;
  sgst?: string | number;
  igst?: string | number;
  cgst_amount?: string | number;
  sgst_amount?: string | number;
  igst_amount?: string | number;
  total?: string | number;
  billing_period_from?: string;
  billing_period_to?: string;
}

export interface InvoiceApiRecord {
  id?: string;
  invoice_title?: string;
  invoice_number?: string;
  invoice_date?: string;
  billing_period_from?: string;
  billing_period_to?: string;
  from_details?: string;
  to_details?: string;
  project?: string;
  client_name?: string;
  notes?: string;
  subtotal?: string | number;
  tax_total?: string | number;
  grand_total?: string | number;
  status?: string;
  followup_status?: string | null;
  is_recurring?: boolean;
  sent?: string | null;
  paid?: string | null;
  items?: InvoiceApiItem[];
  created_on?: string;
  updated_on?: string;
}

export type InvoiceFollowupStatus = "draft" | "in-progress" | "send" | null;

export function normalizeInvoiceFollowupStatus(
  value?: string | null
): InvoiceFollowupStatus {
  const normalized = value?.trim().toLowerCase().replace(/_/g, "-") ?? "";

  if (!normalized) {
    return null;
  }

  if (normalized === "in-progress" || normalized === "inprogress") {
    return "in-progress";
  }

  if (normalized === "draft") {
    return "draft";
  }

  if (normalized === "send" || normalized === "sent") {
    return "send";
  }

  return null;
}

function parseAmount(value?: string | number): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function parseInvoiceAmount(value?: string | number): number {
  return parseAmount(value);
}

function formatRateLabel(value?: string | number): string {
  const rate = parseAmount(value);
  if (rate <= 0) {
    return "";
  }

  return Number.isInteger(rate) ? `${rate}%` : `${rate.toFixed(1)}%`;
}

export function formatInvoiceListDate(value?: string): string {
  if (!value?.trim()) {
    return "—";
  }

  const date = parseDateInputValue(value);
  if (!date) {
    return value;
  }

  return formatPreviewDate(date);
}

export function formatInvoiceItemTaxLabel(item: InvoiceApiItem): string {
  const parts: string[] = [];

  if (parseAmount(item.cgst_amount) > 0) {
    parts.push(`CGST ${formatRateLabel(item.cgst)}`);
  }

  if (parseAmount(item.sgst_amount) > 0) {
    parts.push(`SGST ${formatRateLabel(item.sgst)}`);
  }

  if (parseAmount(item.igst_amount) > 0) {
    parts.push(`IGST ${formatRateLabel(item.igst)}`);
  }

  return parts.length > 0 ? parts.join(" · ") : "—";
}

export function getInvoiceItemTaxAmount(item: InvoiceApiItem): number {
  return (
    parseAmount(item.cgst_amount) +
    parseAmount(item.sgst_amount) +
    parseAmount(item.igst_amount)
  );
}

export function formatInvoiceItemBillingPeriod(
  item: Pick<InvoiceApiItem, "billing_period_from" | "billing_period_to">
): string {
  const from = item.billing_period_from?.trim();
  const to = item.billing_period_to?.trim();

  if (from && to) {
    return `${formatInvoiceListDate(from)} – ${formatInvoiceListDate(to)}`;
  }

  if (from) {
    return formatInvoiceListDate(from);
  }

  if (to) {
    return formatInvoiceListDate(to);
  }

  return "";
}

export function parseInvoiceItemDescription(
  description?: string,
  fallbackBillingPeriod?: string
): {
  title: string;
  subtitle: string;
  billingPeriod: string;
} {
  const parsed = parseLineItemDescription(description);

  if (!parsed.billingPeriod?.trim() && fallbackBillingPeriod?.trim()) {
    return {
      ...parsed,
      billingPeriod: fallbackBillingPeriod.trim(),
    };
  }

  return parsed;
}

export interface ParsedInvoiceDetails {
  name: string;
  lines: string[];
  gstin: string;
}

function extractGstinPart(part: string): string {
  return part.replace(/^(GSTIN|GST\s*NO)\s*:\s*/i, "").trim();
}

function isGstinPart(part: string): boolean {
  return /^(GSTIN|GST\s*NO)\s*:/i.test(part);
}

function isGstTypePart(part: string): boolean {
  return /^GST Type\s*:/i.test(part);
}

function filterInvoiceDetailLines(lines: string[]): string[] {
  return lines.filter(part => !isGstTypePart(part));
}

function isLabeledDetailPart(part: string): boolean {
  return /^(Mobile|Email)\s*:/i.test(part);
}

function groupLegacyAddressParts(parts: string[]): string[] {
  if (parts.length === 0) {
    return [];
  }

  if (parts.some(isLabeledDetailPart)) {
    return parts;
  }

  const lines: string[] = [];
  let index = 0;

  while (index < parts.length) {
    const remaining = parts.length - index;
    const chunkSize = remaining <= 4 ? remaining : 4;
    lines.push(parts.slice(index, index + chunkSize).join(", "));
    index += chunkSize;
  }

  return lines;
}

function parseNewlineInvoiceDetails(details: string): ParsedInvoiceDetails {
  const parts = details
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .filter(part => !isGstTypePart(part));

  if (parts.length === 0) {
    return { name: "—", lines: [], gstin: "" };
  }

  let gstin = "";
  const contentParts = [...parts];

  if (isGstinPart(contentParts[contentParts.length - 1] ?? "")) {
    gstin = extractGstinPart(contentParts.pop() ?? "");
  }

  const name = contentParts[0] ?? "—";
  const lines = filterInvoiceDetailLines(contentParts.slice(1));

  return { name, lines, gstin };
}

export function parseInvoiceDetails(details?: string): ParsedInvoiceDetails {
  const text = details?.trim();

  if (!text) {
    return { name: "—", lines: [], gstin: "" };
  }

  if (text.includes("\n")) {
    return parseNewlineInvoiceDetails(text);
  }

  const normalizedText = text.replace(/,\s*,/g, ",\n");
  if (normalizedText.includes("\n")) {
    return parseNewlineInvoiceDetails(normalizedText);
  }

  const parts = text
    .split(",")
    .map(part => part.trim())
    .filter(Boolean)
    .filter(part => !isGstTypePart(part));

  if (parts.length === 0) {
    return { name: "—", lines: [], gstin: "" };
  }

  const gstinIndex = parts.findIndex(isGstinPart);
  let gstin = "";
  const addressParts = [...parts];

  if (gstinIndex >= 0) {
    gstin = extractGstinPart(addressParts[gstinIndex]);
    addressParts.splice(gstinIndex, 1);
  }

  const name = addressParts[0] ?? "—";
  const lines = groupLegacyAddressParts(addressParts.slice(1));

  return { name, lines, gstin };
}

function displayText(value?: string): string {
  const trimmed = value?.trim();
  return trimmed ?? "—";
}

function extractClientName(record: InvoiceApiRecord): string {
  if (record.client_name?.trim()) {
    return record.client_name.trim();
  }

  const details = record.to_details?.trim();
  if (!details) {
    return "—";
  }

  return details.split(",")[0]?.trim() || "—";
}

function formatInvoicePeriod(fromValue?: string, toValue?: string): string {
  const formatDate = (value: string) => {
    const date = parseDateInputValue(value);
    if (!date) {
      return value;
    }

    return formatPreviewDate(date);
  };

  const from = fromValue?.trim();
  const to = toValue?.trim();

  if (from && to) {
    return `${formatDate(from)} – ${formatDate(to)}`;
  }

  if (from) {
    return formatDate(from);
  }

  if (to) {
    return formatDate(to);
  }

  return "—";
}

function normalizeInvoiceStatus(status?: string): InvoiceStatus {
  const value = status?.trim().toLowerCase() ?? "";

  if (!value) {
    return "Draft";
  }

  if (value === "paid") {
    return "Paid";
  }

  if (value === "sent") {
    return "Sent";
  }

  if (value === "overdue") {
    return "Overdue";
  }

  if (
    value === "pending review" ||
    value === "pending_review" ||
    value === "pending"
  ) {
    return "Pending";
  }

  if (value === "on hold" || value === "on_hold" || value === "onhold") {
    return "On Hold";
  }

  return "Draft";
}

export function normalizeInvoiceRecord(record: InvoiceApiRecord): Invoice {
  const invoiceNumber = displayText(record.invoice_number);
  const recordId = record.id?.trim();

  return {
    id: invoiceNumber !== "—" ? invoiceNumber : (recordId ?? "—"),
    client: extractClientName(record),
    period: formatInvoicePeriod(
      record.billing_period_from,
      record.billing_period_to
    ),
    amount: parseAmount(record.subtotal),
    gst: parseAmount(record.tax_total),
    total: parseAmount(record.grand_total),
    status: normalizeInvoiceStatus(record.status),
    sent: record.sent ?? null,
    paid: record.paid ?? null,
  };
}

export function extractInvoiceRecords(response: unknown): InvoiceApiRecord[] {
  if (!response) {
    return [];
  }

  const list = Array.isArray(response)
    ? response
    : ((response as { data?: unknown[]; results?: unknown[] })?.data ??
      (response as { results?: unknown[] })?.results ??
      []);

  if (!Array.isArray(list)) {
    return [];
  }

  return list.filter(
    (item): item is InvoiceApiRecord =>
      Boolean(item) && typeof item === "object"
  );
}

export function normalizeInvoiceList(response: unknown): Invoice[] {
  return extractInvoiceRecords(response).map(normalizeInvoiceRecord);
}
